import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Check, X, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Bouton unifié "Garder" / "Rejeter".
 *
 * Source unique de vérité — appelle /api/admin/candidates/{id}/keep ou /reject.
 * Réutilisé partout : fiche candidat, liste, mobile, decision card.
 *
 * Props:
 *   - candidateId : string (required)
 *   - currentStatus : string (optional, used for label customisation)
 *   - size : "sm" | "default" | "lg"
 *   - onChanged : (newStatus, prevStatus) => void
 *   - layout : "row" (default) | "stacked"
 *   - keepLabel / rejectLabel : labels custom
 */
const KeepRejectActions = ({
  candidateId,
  currentStatus = null,
  size = 'default',
  onChanged,
  layout = 'row',
  keepLabel = 'Garder',
  rejectLabel = 'Rejeter',
  hideReject = false,
  hideKeep = false,
  testIdPrefix = '',
}) => {
  const { token } = useAuth();
  const [busy, setBusy] = useState(null); // 'keep' | 'reject' | null

  const callAction = async (action) => {
    if (!candidateId) return;
    setBusy(action);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/candidates/${candidateId}/${action}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const verb = action === 'keep' ? 'gardé' : 'rejeté';
      toast.success(`Candidat ${verb} → ${data.status_new}`);
      if (onChanged) onChanged(data.status_new, data.status_prev);
    } catch (e) {
      toast.error(`Action échouée : ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const isRejected = currentStatus === 'rejected' || currentStatus === 'rejected_auto' || currentStatus === 'archived';
  const finalKeepLabel = isRejected ? 'Reprendre' : keepLabel;

  const wrapperClass = layout === 'stacked'
    ? 'flex flex-col gap-2 w-full'
    : 'flex flex-wrap gap-2';

  return (
    <div className={wrapperClass} data-testid={`${testIdPrefix}keep-reject-actions`}>
      {!hideKeep && (
        <Button
          size={size}
          onClick={() => callAction('keep')}
          disabled={!!busy}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid={`${testIdPrefix}keep-btn`}
        >
          {busy === 'keep' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
          {finalKeepLabel}
        </Button>
      )}
      {!hideReject && !isRejected && (
        <Button
          size={size}
          variant="outline"
          onClick={() => callAction('reject')}
          disabled={!!busy}
          className="border-red-300 text-red-700 hover:bg-red-50"
          data-testid={`${testIdPrefix}reject-btn`}
        >
          {busy === 'reject' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <X className="h-4 w-4 mr-2" />}
          {rejectLabel}
        </Button>
      )}
    </div>
  );
};

export default KeepRejectActions;
